import {
  Component,
  ComponentRef,
  DestroyRef,
  Injector,
  OnInit,
  ViewChild,
  ViewContainerRef,
  effect,
  forwardRef,
  inject,
  input
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormArray,
  FormControl,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  NgControl,
  ReactiveFormsModule,
  ValidationErrors,
  Validator
} from '@angular/forms';
import { Subscription } from 'rxjs';

import {
  FormBuilderData,
  FormBuilderItem,
  FormBuilderSection
} from '@shared/components/row-edition-form/form-builder/form-builder.model';
import { getValidators } from '@shared/components/row-edition-form/form-builder/form-builder.utils';
import { FormMode } from '@shared/components/row-edition-form/row-edition-form.utils';

@Component({
  selector: 'app-tagged-section',
  imports: [ReactiveFormsModule],
  templateUrl: './tagged-section.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TaggedSectionComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => TaggedSectionComponent),
      multi: true
    }
  ]
})
export class TaggedSectionComponent implements ControlValueAccessor, Validator, OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);

  @ViewChild('outlet', { read: ViewContainerRef, static: true }) outlet!: ViewContainerRef;

  fields = input.required<FormBuilderItem[]>();
  discriminatorField = input.required<string>();
  discriminatorValue = input.required<string>();
  formMode = input.required<FormMode>();
  rowId = input<string>('');

  internalForm!: FormGroup;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private formBuilderRef: ComponentRef<any> | null = null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private onChange: (value: any) => void = () => {};
  private onTouched: () => void = () => {};
  private initialized = false;
  private valueChangesSub: Subscription | null = null;
  private loadGeneration = 0;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private lastWrittenValue: any = null;
  /** `undefined` until the first lookup; `null` when this section has no host control. */
  private ngControl: NgControl | null | undefined;

  constructor() {
    effect(() => {
      // Read signals to track them
      this.fields();
      this.discriminatorValue();

      if (!this.initialized) {
        return;
      }

      this.rebuild();
    });

    this.destroyRef.onDestroy(() => {
      this.valueChangesSub?.unsubscribe();
    });
  }

  ngOnInit(): void {
    this.initialized = true;
    this.rebuild();
  }

  /**
   * The control this section is bound to, or null when it is used without one.
   *
   * Resolved on demand rather than injected as a field or captured in ngOnInit: NgControl
   * injects this component through NG_VALUE_ACCESSOR, so asking for it during construction
   * is a cycle — and its `control` is still unassigned during this component's ngOnInit,
   * because the binding directive's own setup runs after it. Reading through the directive
   * every time means the control is picked up as soon as it exists.
   */
  private get hostControl(): AbstractControl | null {
    if (this.ngControl === undefined) {
      this.ngControl = this.injector.get(NgControl, null, { optional: true, self: true });
    }
    return this.ngControl?.control ?? null;
  }

  private rebuild(): void {
    this.valueChangesSub?.unsubscribe();

    this.internalForm = this.buildFormGroup(this.fields());
    this.applyLastValue();

    this.valueChangesSub = this.internalForm.valueChanges.subscribe(() => {
      // Touched first: emitValue publishes synchronously, and consumers gate on `touched`
      // to tell a user edit from form construction. Touching afterwards makes the first
      // edit arrive looking programmatic, so it is dropped.
      this.onTouched();
      this.emitValue({ userInitiated: true });
    });

    if (this.formBuilderRef) {
      this.formBuilderRef.destroy();
      this.formBuilderRef = null;
    }
    this.outlet.clear();

    this.loadFormBuilder();
    this.emitValue({ userInitiated: false });
  }

  private async loadFormBuilder(): Promise<void> {
    const gen = ++this.loadGeneration;
    const { FormBuilderComponent } = await import('../form-builder/form-builder.component');
    if (gen !== this.loadGeneration) return;
    this.formBuilderRef = this.outlet.createComponent(FormBuilderComponent);
    this.formBuilderRef.setInput('schema', { items: this.fields() });
    this.formBuilderRef.setInput('form', this.internalForm);
    this.formBuilderRef.setInput('formMode', this.formMode());
    this.formBuilderRef.setInput('singleColumn', true);
    this.formBuilderRef.setInput('rowId', this.rowId());
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  writeValue(value: any): void {
    this.lastWrittenValue = value && typeof value === 'object' ? value : null;

    if (!this.initialized || !value || typeof value !== 'object') {
      return;
    }

    this.applyLastValue();
  }

  private applyLastValue(): void {
    const value = this.lastWrittenValue;
    if (!value) return;

    // Pre-populate FormArrays before patching — patchValue is a no-op on empty FormArrays
    for (const field of this.fields()) {
      if (!('items' in field) || !field.isArray) continue;
      const section = field as FormBuilderSection;
      const arrayValue = value[section.id];
      if (!Array.isArray(arrayValue)) continue;
      const formArray = this.internalForm.get(section.id) as FormArray;
      if (!formArray) continue;
      formArray.clear({ emitEvent: false });
      for (const _item of arrayValue) {
        formArray.push(this.buildFormGroup(section.items), { emitEvent: false });
      }
    }

    const patch: Record<string, unknown> = {};
    for (const field of this.fields()) {
      if ('id' in field && value[field.id] !== undefined) {
        patch[field.id] = value[field.id];
      }
    }
    this.internalForm.patchValue(patch, { emitEvent: false });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  registerOnChange(fn: any): void {
    this.onChange = fn;
    if (this.initialized) {
      Promise.resolve().then(() => this.emitValue({ userInitiated: false }));
    }
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  validate(): ValidationErrors | null {
    if (!this.initialized || !this.internalForm) {
      return null;
    }
    return this.internalForm.valid ? null : { taggedSection: true };
  }

  /**
   * Pushes the discriminator-composed value up to the host control.
   *
   * A user edit goes through `onChange`, whose pipeline marks the control dirty — which is
   * what an edit should do. The composition this section performs on its own, at init and
   * on every rebuild, must not: an untouched form would report itself as edited and the
   * form-builder's `invalid && (dirty || touched)` banner would fire on a required field
   * nobody has seen. That write therefore goes straight onto the control, never entering
   * the change pipeline — `setValue` moves the value without touching interaction state.
   * Marking dirty and undoing it afterwards is not equivalent — change detection reads the
   * interaction state in between and renders the banner it saw.
   *
   * The write still emits: switching variant rebuilds the section, and the resulting
   * validity is what decides whether Save is offered. Silencing it would leave the form's
   * `statusChanges` describing the variant the user just left.
   */
  private emitValue({ userInitiated }: { userInitiated: boolean }): void {
    const formValue = this.internalForm.getRawValue();
    const composed = {
      [this.discriminatorField()]: this.discriminatorValue(),
      ...formValue
    };

    if (userInitiated || !this.hostControl) {
      this.onChange(composed);
      return;
    }
    this.hostControl.setValue(composed);
  }

  private buildFormGroup(items: FormBuilderItem[]): FormGroup {
    const group: Record<string, FormControl | FormGroup | FormArray> = {};

    for (const item of items) {
      if ('type' in item) {
        const validators = getValidators(item as FormBuilderData);
        group[item.id] = new FormControl(null, validators);
      } else if ('items' in item) {
        if (item.isArray) {
          group[item.id] = new FormArray<FormControl>([]);
        } else {
          group[item.id] = this.buildFormGroup((item as FormBuilderSection).items);
        }
      }
    }

    return new FormGroup(group);
  }
}
